/**
 * POS Settings Modal
 * 
 * Allows configuration of POS settings like printer, cash management, etc.
 */

import { useState, useEffect } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { 
  usePOSSettings, 
  useUpdatePOSSettings, 
  type POSSettings,
  DEFAULT_POS_SETTINGS 
} from '@/api/settings'

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

export function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const { selectedStore } = useSessionStore()
  const storeId = selectedStore?.id || null

  // Fetch current settings - only when modal is open
  const { data: settings, isLoading } = usePOSSettings(storeId)
  const updateSettings = useUpdatePOSSettings()

  // Local state for form
  const [formData, setFormData] = useState<Partial<POSSettings>>({})

  // Initialize form data when settings load
  useEffect(() => {
    if (settings) {
      setFormData(settings)
    }
  }, [settings])

  const handleChange = (key: keyof POSSettings, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({
        settings: formData,
        storeId
      })
      onClose()
    } catch (error) {
      // Show error message
      console.error('Failed to save settings:', error)
    }
  }

  const handleReset = () => {
    setFormData(settings || {})
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">POS Configuration</h2>
          <p className="text-sm text-gray-500 mt-1">
            Configure settings for {selectedStore?.name || 'this store'}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Printer Settings */}
              <section>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Printer Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Receipt Width (characters)
                    </label>
                    <input
                      type="number"
                      value={formData['pos.printer.width'] ?? DEFAULT_POS_SETTINGS['pos.printer.width']}
                      onChange={(e) => handleChange('pos.printer.width', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="32"
                      max="80"
                    />
                    <p className="text-xs text-gray-500 mt-1">Typical values: 32, 42, 48, 80</p>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="print-clerk"
                      checked={formData['frontend.print_receipt_clerk'] ?? DEFAULT_POS_SETTINGS['frontend.print_receipt_clerk']}
                      onChange={(e) => handleChange('frontend.print_receipt_clerk', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="print-clerk" className="ml-2 block text-sm text-gray-700">
                      Print clerk name on receipt
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="print-congrats"
                      checked={formData['pos.printer.print_congratulations_line'] ?? DEFAULT_POS_SETTINGS['pos.printer.print_congratulations_line']}
                      onChange={(e) => handleChange('pos.printer.print_congratulations_line', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="print-congrats" className="ml-2 block text-sm text-gray-700">
                      Print congratulations message
                    </label>
                  </div>

                  <div>
                    <label htmlFor="receipt-complete-action" className="block text-sm font-medium text-gray-700 mb-1">
                      Receipt end action
                    </label>
                    <select
                      id="receipt-complete-action"
                      value={formData['pos.receipt.complete_action'] ?? DEFAULT_POS_SETTINGS['pos.receipt.complete_action']}
                      onChange={(e) => handleChange('pos.receipt.complete_action', e.target.value as 'ask' | 'print' | 'nothing')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="ask">Ask clerk</option>
                      <option value="print">Print receipt</option>
                      <option value="nothing">Do nothing</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">What happens after a successful sale</p>
                  </div>
                </div>
              </section>

              {/* Cash Management */}
              <section>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Cash Management</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Starting Cash Amount
                    </label>
                    <input
                      type="number"
                      value={formData['frontend.starting_cash'] ?? DEFAULT_POS_SETTINGS['frontend.starting_cash']}
                      onChange={(e) => handleChange('frontend.starting_cash', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="0"
                      step="0.01"
                    />
                    <p className="text-xs text-gray-500 mt-1">Default cash amount in drawer at start of shift</p>
                  </div>

                  {/* `pos.cashout_by_clerk` toggle intentionally removed
                      from this UI. The flag fundamentally mismatches the
                      shared-drawer workflow used at every waio store --
                      multiple clerks ring sales on one drawer, only the
                      closer cashes out. When the flag is on, the BE
                      filters cash-in lookups and sales by the requester's
                      personnel_id, so a closing clerk sees `$0 starting
                      cash` (no clock-in by them personally) and their
                      sales window excludes the earlier clerks',
                      producing a phantom variance that equals the
                      filtered-out clerks' contributions to the drawer
                      (Ridgeway 2026-05-26 was the production trigger:
                      +$256 cash variance + $99.83 card variance, all
                      explained by this filter). The flag has no
                      operational benefit at any current waio store; per-
                      clerk sales attribution lives in BI reports, not
                      drawer reconciliation. Setting via SQL / admin only
                      from here on so accidental UI toggles can't put a
                      store back into the broken configuration. */}

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="always-open-drawer"
                      checked={formData['frontend.always_open_drawer'] ?? DEFAULT_POS_SETTINGS['frontend.always_open_drawer']}
                      onChange={(e) => handleChange('frontend.always_open_drawer', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="always-open-drawer" className="ml-2 block text-sm text-gray-700">
                      Always open cash drawer on sale
                    </label>
                  </div>
                </div>
              </section>

              {/* Session Settings */}
              <section>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Session Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Session Timeout (hours)
                    </label>
                    <input
                      type="number"
                      value={formData['pos.jwt_expiry_hours'] ?? DEFAULT_POS_SETTINGS['pos.jwt_expiry_hours']}
                      onChange={(e) => handleChange('pos.jwt_expiry_hours', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="1"
                      max="24"
                    />
                    <p className="text-xs text-gray-500 mt-1">How long before clerk needs to re-authenticate</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Refresh Token Expiry (days)
                    </label>
                    <input
                      type="number"
                      value={formData['pos.jwt_refresh_days'] ?? DEFAULT_POS_SETTINGS['pos.jwt_refresh_days']}
                      onChange={(e) => handleChange('pos.jwt_refresh_days', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="1"
                      max="30"
                    />
                    <p className="text-xs text-gray-500 mt-1">How long refresh tokens remain valid</p>
                  </div>

                  {/* BE quirk: portal stores `pos.logout_timeout` as
                      milliseconds despite the field labeling itself
                      "seconds". Divide here so what we render matches
                      what the operator typed in the portal. Mirror
                      conversion in usePOSConfig. If portal is fixed,
                      remove BOTH conversions. */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Auto-logout After Inactivity (seconds)
                    </label>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600">
                      {typeof formData['pos.logout_timeout'] === 'number' && formData['pos.logout_timeout'] > 0
                        ? `${Math.round(formData['pos.logout_timeout'] / 1000)}`
                        : 'Disabled'}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Seconds of inactivity before the POS returns to the PIN view. Set in waio-portal.
                    </p>
                  </div>
                </div>
              </section>

              {/* Customer Settings */}
              <section>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="customer-required"
                      checked={formData['frontend.customer_required'] ?? DEFAULT_POS_SETTINGS['frontend.customer_required']}
                      onChange={(e) => handleChange('frontend.customer_required', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="customer-required" className="ml-2 block text-sm text-gray-700">
                      Require customer for every sale
                    </label>
                  </div>
                </div>
              </section>

              {/* Discount Settings */}
              <section>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Discount Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="discounts-disabled"
                      checked={formData['pos.discounts_disabled'] ?? DEFAULT_POS_SETTINGS['pos.discounts_disabled']}
                      onChange={(e) => handleChange('pos.discounts_disabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="discounts-disabled" className="ml-2 block text-sm text-gray-700">
                      Disable all discounts
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="rewards-on-final"
                      checked={formData['pos.apply_rewards_discounts_on_final_total'] ?? DEFAULT_POS_SETTINGS['pos.apply_rewards_discounts_on_final_total']}
                      onChange={(e) => handleChange('pos.apply_rewards_discounts_on_final_total', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="rewards-on-final" className="ml-2 block text-sm text-gray-700">
                      Apply rewards discounts on final total
                    </label>
                  </div>
                </div>
              </section>

              {/* Display Settings */}
              <section>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Display Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="hide-out-of-stock"
                      checked={formData['frontend.hide_out_of_stock'] ?? DEFAULT_POS_SETTINGS['frontend.hide_out_of_stock']}
                      onChange={(e) => handleChange('frontend.hide_out_of_stock', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="hide-out-of-stock" className="ml-2 block text-sm text-gray-700">
                      Hide out of stock products
                    </label>
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={updateSettings.isPending}
          >
            Reset
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={updateSettings.isPending}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={updateSettings.isPending}
          >
            {updateSettings.isPending ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  )
}
